import { SIDE_KINDS } from '@felix/domain';
import type { Voyage, VoyageReport } from './types.js';
import { assignedVoyageIdOf } from './voyage-record.js';

/**
 * Kalkıştan sefere zincir ve bağlam sürekliliği (ADR 0157 §1–§4). Saf fonksiyonlar: sefersiz
 * kalkışın "Yeni sefer" kararı (ADR 0058 §3) yalnız rapora yazılır; sefer ancak açılınca ya da
 * bağlanınca başlar. Burada başlatılmamış kalkış, onu izleyen sefersiz raporlar ve bağlanabilecek
 * seferler türetilir; hiçbiri saklanmaz.
 */

const byTime = (a: VoyageReport, b: VoyageReport) =>
  Date.parse(a.reportedAt) - Date.parse(b.reportedAt) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

const live = (reports: readonly VoyageReport[]) =>
  reports.filter((r) => r.status !== 'void').sort(byTime);

/** Raporun seferi: yeni sunucuda `assignedVoyageId`, eski sunucuda kalkış bağı (ADR 0083 §2). */
const voyageOfReport = (report: VoyageReport, voyages: readonly Voyage[]) =>
  assignedVoyageIdOf(report, voyages);

/**
 * Gönderimden sonra sefer formu açılır mı (ADR 0157 §1, #2260): "Yeni sefer" kararlı, kendi seferi
 * ve bağı olmayan kalkış. Yeniden açılıp gönderilen bağlı kalkışta açılmaz.
 */
export function startsVoyageAfterSubmit(report: {
  kind: string;
  voyageId: string | null;
  assignedVoyageId?: string | null;
  data: Readonly<Record<string, unknown>>;
}): boolean {
  return (
    report.kind === 'departure' &&
    report.voyageId === null &&
    (report.assignedVoyageId ?? null) === null &&
    report.data.voyageAction === 'create'
  );
}

/**
 * Kalkış ve onu izleyen sefersiz raporlar (ADR 0157 §2): başka sefere bağlı ilk rapora kadar;
 * iptal edilmiş rapor atlanır, taslaklar sayılır. Kalkış bulunamazsa boş.
 */
export function queueFrom(
  timeline: readonly VoyageReport[],
  departureId: string,
  voyages: readonly Voyage[],
): VoyageReport[] {
  const list = live(timeline);
  const start = list.findIndex((r) => r.id === departureId);
  if (start < 0) return [];
  const out: VoyageReport[] = [];
  for (const report of list.slice(start)) {
    if (voyageOfReport(report, voyages) !== null) break;
    out.push(report);
  }
  return out;
}

export interface PendingVoyageStart {
  /** En yeni başlatılmamış "Yeni sefer" kalkışı. */
  departure: VoyageReport;
  /** Başlatılmamış kalkış sayısı (gürültü: yalnız en yenisi ve sayı gösterilir). */
  count: number;
  /** Kalkış ve onu izleyen sefersiz raporlar: birlikte bağlanır. */
  queue: VoyageReport[];
  /**
   * Bağlanabilecek seferler: raporsuz planlanan seferler ve kuyruktan hemen sonra başlayan sefer
   * (arada yalnız sefersiz rapor varsa; ADR 0157 §4).
   */
  targets: Voyage[];
}

/**
 * Seferi başlatılmamış kalkış (ADR 0157 §2, #2262): gönderilmiş, iptal edilmemiş, seferi ve bağı
 * olmayan, kararı "Yeni sefer" olan kalkış. Yoksa `null`.
 */
export function pendingVoyageStart(
  timeline: readonly VoyageReport[],
  voyages: readonly Voyage[],
): PendingVoyageStart | null {
  const list = live(timeline);
  const pending = list.filter(
    (r) =>
      r.kind === 'departure' &&
      r.status === 'submitted' &&
      r.voyageId === null &&
      r.data.voyageAction === 'create' &&
      voyageOfReport(r, voyages) === null,
  );
  const departure = pending.at(-1);
  if (!departure) return null;
  return { departure, count: pending.length, ...startOptions(list, departure.id, voyages) };
}

/**
 * Sefersiz kalkışın bağlama seçenekleri (ADR 0157 §4, #2269): kuyruk (kalkış ve onu izleyen
 * sefersizler) ve hedefler: raporsuz, kalkışsız planlanan seferler ile kuyruktan hemen sonra
 * başlayan yazılabilir sefer. Sunucunun bağ kuralları (`REPORT_LINKED`, `VOYAGE_OVERLAP`) aynen.
 */
export function startOptions(
  timeline: readonly VoyageReport[],
  departureId: string,
  voyages: readonly Voyage[],
): { queue: VoyageReport[]; targets: Voyage[] } {
  const list = live(timeline);
  const queue = queueFrom(list, departureId, voyages);
  const last = queue.at(-1);
  const after = last ? list[list.indexOf(last) + 1] : undefined;
  const nextVoyageId = after ? voyageOfReport(after, voyages) : null;
  const hasReports = new Set(
    list.map((r) => voyageOfReport(r, voyages)).filter((id): id is string => id !== null),
  );
  const writable = (v: Voyage) =>
    (v.status === 'active' || v.status === 'planned') && !v.voidedAt && !v.cancelledAt;
  const targets = voyages.filter(
    (v) =>
      writable(v) &&
      (v.id === nextVoyageId ||
        (v.status === 'planned' && !v.departureReportId && !hasReports.has(v.id))),
  );
  return { queue, targets };
}

/**
 * Bağlam sürekliliği (ADR 0157 §3, #2264): gemide `?voyage=` verilmeden açılan yeni raporun
 * bağlamı geminin son akış raporundan gelir. Son akış raporu: yan olaylar ve iptal dışında en son
 * rapor, taslaklar dahil (ADR 0083 §1). Dönüş: sefer kimliği, sefersizse `null`; rapor yoksa ya da
 * raporun seferi yazılamıyorsa `undefined` (eski kural: süren, yoksa planlanan; ADR 0047 §1).
 */
export function contextVoyageOf(
  timeline: readonly VoyageReport[],
  voyages: readonly Voyage[],
): string | null | undefined {
  const last = live(timeline)
    .filter((r) => !SIDE_KINDS.has(r.kind))
    .at(-1);
  if (!last) return undefined;
  const id = voyageOfReport(last, voyages);
  if (id === null) return null;
  const voyage = voyages.find((v) => v.id === id);
  return voyage && (voyage.status === 'active' || voyage.status === 'planned') ? id : undefined;
}
